import type { OrgMember } from '@/types/api';

/**
 * Audience tabs for Settings → Users (A2F-698).
 *
 * Rendering rule: tabs only show for orgs marked `is_public_signup=true`
 * (CTDCommons today) AND only when at least one Pro member exists. Every
 * other org renders the page as a single-audience list, identical to the
 * pre-A2F-698 UX.
 *
 * The build prompt originally proposed a `Main | Plugin | All` split for
 * future non-public mixed-audience orgs. That branch was removed because
 * (a) no such org exists today, (b) the Pro signal is currently only
 * populated in the public-signup org via `signup_source='chrona_pro'` /
 * `style_profiles` / `style_subscriptions`, and (c) speculative tabs that
 * never render are dead code that drifts. When an actual non-public mixed
 * org appears, add the second branch then.
 */

export type Audience = 'commons' | 'pro' | 'all';

export interface AudienceTabConfig {
  tabs: Audience[];
  defaultTab: Audience;
}

/**
 * Pro-signal detection. Mirrors `backend/services/audience.py:is_pro_member`
 * — keep the predicates in sync.
 *
 * Returns true when ANY of:
 *   - `signupSource === 'chrona_pro'` (deterministic, post-migration-058)
 *   - `hasStyleProfile === true` (corroborating, set on first ribbon hydrate)
 *   - `hasStyleSubscription === true` (paid tier signal)
 *
 * False positives are acceptable; false negatives are the failure mode we
 * care about (Pro user appearing in Commons admin view). The three signals
 * are union'd to minimise false negatives.
 *
 * Pre-migration-058 rows with `signupSource='unknown'` AND no
 * `hasStyleProfile` AND no `hasStyleSubscription` are indistinguishable
 * from Commons users via this signal alone — they fall through to the
 * Commons audience. This is a known data-hygiene gap, not a code bug.
 */
export function isProUser(m: OrgMember): boolean {
  return (
    m.signupSource === 'chrona_pro' ||
    m.hasStyleProfile === true ||
    m.hasStyleSubscription === true
  );
}

/**
 * Decide which tabs to show based on the org's signup posture and whether
 * any Pro members exist. Returns `null` when no tabs should render.
 */
export function deriveTabs(
  members: OrgMember[],
  isPublicSignup: boolean,
): AudienceTabConfig | null {
  if (!isPublicSignup) return null;
  if (!members.some(isProUser)) return null;
  return { tabs: ['commons', 'pro', 'all'], defaultTab: 'commons' };
}

/**
 * Filter the member list to the active tab's audience.
 *
 * Mutually exclusive partitions: `commons` = NOT Pro, `pro` = Pro, `all`
 * = both. A "dual" user (someone with both Pro signals AND Commons access)
 * appears only in the Pro tab; admins find them via `all`. Bulk admin
 * actions act on the underlying `org_users` row regardless of which tab
 * the row was selected from, so the dual case is handled correctly by
 * either tab.
 */
export function filterByTab(members: OrgMember[], tab: Audience): OrgMember[] {
  if (tab === 'all') return members;
  if (tab === 'pro') return members.filter(isProUser);
  return members.filter((m) => !isProUser(m));
}

export function tabLabel(tab: Audience): string {
  switch (tab) {
    case 'commons':
      return 'Commons';
    case 'pro':
      return 'Pro';
    case 'all':
      return 'All';
  }
}

/**
 * Roles offered in the Invite User modal's role dropdown.
 *
 * `author` only appears in `is_public_signup` orgs (CTDCommons admins
 * routinely add public-portal users) or orgs that already have at least
 * one author. Other orgs (Lumen today) cannot mint Pro users until they
 * explicitly opt in by some future flow — leaving `author` off the
 * dropdown is the friction that prevents accidental cross-tier invites.
 *
 * Note: A2F-698 follow-up established that Chrona Pro signups land at
 * `role='viewer'` (not `'author'`) so they can also browse Commons. The
 * `author` role here is reserved for admin-driven invites of true
 * ribbon-only users (no web-app access). Keep this in mind if you change
 * the invite flow — minting `author` from this modal is rare and
 * deliberate, not the same as a Pro signup.
 */
export function invitableRoles(
  isPublicSignup: boolean,
  hasExistingAuthor: boolean,
): string[] {
  const base = ['viewer', 'contributor', 'lead'];
  if (isPublicSignup || hasExistingAuthor) {
    return [...base, 'author'];
  }
  return base;
}
